import {
  CustomDimensions,
  getCustomChargeObj,
  getExperienceObj,
  getFeeObj,
  getOptionObj,
  getPromoCode,
} from 'utils/analytics/purchaseHelper';
import { consumer, core, safeToNumber } from '@tock/coretypes';
import { displayInDollars } from '@tock/money';
import { getSettings } from 'settings/AppSettings';
import _ from 'lodash';

export const uaSetupTracking = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  gaWrapper: (...args: any[]) => void,
  tockWebProperty: string,
  googleOptimizeContainerId: string | null,
  customDimensions: CustomDimensions,
  advertisingEnabled: boolean,
  applyAnalyticsReferrerFix: boolean
): void => {
  gaWrapper('create', tockWebProperty, 'auto');
  // This must occur immediately after create
  uaSetAdvertisingFeatures(advertisingEnabled);
  gaWrapper('require', googleOptimizeContainerId);
  gaWrapper('require', 'ec');

  // Set hostname to 'www.exploretock.com' explicitly to mitigate 'hostname = (not set)' inconsistency
  gaWrapper('set', 'hostname', 'www.exploretock.com');

  if (applyAnalyticsReferrerFix && window?.location.pathname.startsWith('/oauth-redir')) {
    // The technique for this is described at
    // https://medium.com/fatllama-engineering/excluding-facebook-and-google-social-sign-in-from-your-referrals-with-google-analytics-ff81588e519
    // Our wrinkle here is that we recognize the where the problem situation
    // can emerge as we're initializing GA.
    gaWrapper('set', 'referrer', getSettings(__ENV__).CONSUMER_URL_BASE);
  }

  setDeviceDimension(gaWrapper, customDimensions.TOCK_APP);
};

const setDeviceDimension = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  gaWrapper: (...args: any[]) => void,
  dimension: string
) => {
  if (window.ReactNative) {
    gaWrapper('set', dimension, 'Tock App (React Native)');
    return;
  }
  gaWrapper('set', dimension, 'Web');
};

export const uaSetAdvertisingFeatures = (advertisingEnabled: boolean) => {
  if (window.ga) {
    if (advertisingEnabled) {
      window.ga('set', 'allowAdFeatures', true);
    } else {
      window.ga('set', 'allowAdFeatures', false);
    }
  }
};

export const uaSetupTrackingRestaurant = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  gaWrapper: (...args: any[]) => void,
  fastRoutingBetweenContext: boolean,
  restaurantWebProperty: string | null,
  trackedDomains: string[]
): void => {
  if (!fastRoutingBetweenContext && restaurantWebProperty) {
    gaWrapper('create', restaurantWebProperty, 'auto', 'restaurant', {
      allowLinker: true,
    });
    // Set hostname to 'www.exploretock.com' explicitly to mitigate 'hostname = (not set)' inconsistency
    gaWrapper('restaurant.set', 'hostname', 'www.exploretock.com');
    gaWrapper('restaurant.require', 'ec');
    if (trackedDomains.length > 0) {
      gaWrapper('restaurant.require', 'linker');
      gaWrapper('restaurant.linker:autoLink', trackedDomains);
    }
  }
};

export const uaAddScript = (googleOptimizeContainerId?: string | null) => {
  if (window.ga) {
    return;
  }

  if (googleOptimizeContainerId) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (function (a: any, s: any, y: any, n: any, c: any, h: any, i?: any, d?: any, e?: any) {
      s.className += ' ' + y;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      h.start = 1 * (new Date() as any);
      h.end = i = function () {
        s.className = s.className.replace(RegExp(' ?' + y), '');
      };
      (a[n] = a[n] || []).hide = h;
      setTimeout(function () {
        i();
        h.end = null;
      }, c);
      h.timeout = c;
    })(window, document.documentElement, 'async-hide', 'dataLayer', 4000, {
      [googleOptimizeContainerId]: true,
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (function (i: any, s, o, g, r, a?: any, m?: any) {
    i.GoogleAnalyticsObject = r;
    (i[r] =
      i[r] ||
      function () {
        // eslint-disable-next-line prefer-rest-params
        (i[r].q = i[r].q || []).push(arguments);
      }),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (i[r].l = 1 * (new Date() as any));
    (a = s.createElement(o)), (m = s.getElementsByTagName(o)[0]);
    a.async = 1;
    a.src = g;
    m.parentNode.insertBefore(a, m);
  })(window, document, 'script', '//www.google-analytics.com/analytics.js', 'ga');
  /* tslint:enable */
};

export const uaSendPurchaseEvent = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  gaWrapper: (...args: any[]) => void,
  purchaseOrder: core.PurchaseOrder,
  purchaseCategory: string,
  business: consumer.BusinessConfig,
  prefix?: string
) => {
  const prefixId = prefix ? `${prefix}.` : '';
  gaWrapper(prefixId + 'set', 'currencyCode', business.currencyCode);
  gaWrapper(
    prefixId + 'ec:addProduct',
    getExperienceObj(purchaseOrder, purchaseCategory, business)
  );

  const optionMap: Dictionary<number> = _.reduce(
    purchaseOrder.optionOrder as core.OptionOrder[],
    (acc: Dictionary<number>, optionOrder: core.OptionOrder) => {
      const optionId = optionOrder.optionId;
      acc[optionId] = (acc[_.toString(safeToNumber(optionId))] || 0) + optionOrder.numPurchased;
      return acc;
    },
    {}
  );

  _.forEach(purchaseOrder.option as core.Price[], (option: core.Price) => {
    gaWrapper(
      prefixId + 'ec:addProduct',
      getOptionObj(option, purchaseCategory, business, optionMap)
    );
  });

  _.forEach(purchaseOrder.fee ?? [], (fee) => {
    gaWrapper(prefixId + 'ec:addProduct', getFeeObj(fee, purchaseCategory, business));
  });

  _.forEach(
    purchaseOrder.customChargeOrder as core.CustomChargeOrder[],
    (customCharge: core.CustomChargeOrder) => {
      gaWrapper(
        prefixId + 'ec:addProduct',
        getCustomChargeObj(customCharge, purchaseCategory, business)
      );
    }
  );

  // Transaction level information is provided via an actionFieldObject.
  gaWrapper(prefixId + 'ec:setAction', 'purchase', {
    id: `Res_${safeToNumber(purchaseOrder.id)}`,
    revenue: displayInDollars(purchaseOrder.totalPrice),
    coupon: getPromoCode(purchaseOrder),
  });
};

export const uaSendConversionEvent = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  gaWrapper: (...args: any[]) => void,
  purchaseOrder: core.PurchaseOrder,
  prefix?: string
) =>
  gaWrapper(
    (prefix ? `${prefix}.` : '') + 'send',
    'event',
    'conversion',
    'reservation',
    purchaseOrder.ticketTypeName,
    _.toInteger(displayInDollars(purchaseOrder.totalPrice))
  );

export const uaTransitionToPage = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  gaTrack: (...args: any[]) => void,
  sanitizedLocation: string,
  origin: string
) => {
  gaTrack('set', 'page', sanitizedLocation);
  gaTrack('set', 'location', `${origin || ''}${sanitizedLocation}`);
  gaTrack('send', 'pageview');
};
